import { useMemo } from 'react';

import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import classNames from 'classnames';

import { getIsEnabledByProcedureName } from '@/modules/patientList/patientStrip/patientStripOdontogram/utils';
import { usePreferences } from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { IconPill } from './IconPill';

interface StageConfig {
  displayStage: string;
  status: string;
  definition: string;
  summary?: string;
  criteria?: string[];
}

const useStageConfig = (stage?: number): StageConfig => {
  const { t } = useLingui();

  return useMemo(() => {
    switch (stage) {
      case 1:
        return {
          displayStage: '1',
          status: t`Initial`,
          definition: t`Early periodontitis with minimal tissue and bone loss.`,
          summary: t`An SRP recommendation was given, and no higher stage thresholds were met.`,
        };
      case 2:
        return {
          displayStage: '2',
          status: t`Moderate`,
          definition: t`Established disease with moderate attachment and bone loss.`,
          summary: t`This visit met SRP criteria and Stage 2 thresholds.`,
          criteria: [t`Max CAL >= 5 mm`, t`Max RBL >= 15%`],
        };
      case 3:
        return {
          displayStage: '3',
          status: t`Severe`,
          definition: t`Advanced disease with high risk for tooth loss and increased complexity.`,
          summary: t`This visit met SRP criteria and Stage 3 thresholds.`,
          criteria: [t`Max CAL >= 6 mm`, t`Max RBL >= 33%`],
        };
      case 4:
        return {
          displayStage: '4',
          status: t`Advanced`,
          definition: t`Extensive disease affecting function, stability, and long-term prognosis.`,
          summary: t`This visit met SRP criteria and Stage 4 thresholds.`,
          criteria: [
            t`Max CAL >= 6 mm`,
            t`Max RBL >= 33%`,
            t`Missing 5+ non-wisdom teeth`,
          ],
        };
      default:
        return {
          displayStage: 'N/A',
          status: t`No Periodontitis Detected`,
          definition: t`Stages are assigned only when an SRP recommendation is given.`,
        };
    }
  }, [stage, t]);
};

interface Props {
  examDate: string | null;
  hasCalculus: boolean;
  isPerioPatient: boolean;
  mode?: 'dark' | 'light';
  numberOfQuadrantsWithPerioRecs: number;
  stage?: number;
  toothCount: number;
}

export const StageIconPill = ({
  examDate,
  hasCalculus,
  isPerioPatient,
  mode = 'dark',
  numberOfQuadrantsWithPerioRecs,
  stage,
  toothCount,
}: Props) => {
  const { data: preferences } = usePreferences();
  const isLocSrpEnabled = getIsEnabledByProcedureName('locPerio', preferences);
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const stageConfig = useStageConfig(stage);
  const sectionHeadingClassName = classNames(
    'vai-inline-flex vai-items-center vai-rounded-md vai-border vai-px-2 vai-font-inter vai-text-[11px] vai-font-semibold vai-uppercase vai-tracking-[0.08em]',
    {
      'vai-border-zinc-500 vai-bg-zinc-800/40 vai-text-zinc-200':
        mode === 'dark',
      'vai-border-zinc-300 vai-bg-zinc-50 vai-text-zinc-700': mode === 'light',
    },
  );

  return (
    <Tooltip
      onOpenChange={(open) => {
        if (open) {
          trackEvent(mixpanelEvents.perioChartStageTooltipOpen, {
            perioStage: stage ?? 'N/A',
            examDate,
            hasCalculusAtBalancedThreshold: hasCalculus,
            isPerioPatient,
            numberOfQuadrantsWithPerioRecs,
            toothCount,
          });
        }
      }}
    >
      <TooltipTrigger
        className="vai-flex vai-items-center vai-justify-center"
        tabIndex={-1} // tabIndex prevents tooltip from opening on focus
      >
        <IconPill
          mode={mode}
          text={stage ? t`Stage` : t`Stage N/A`}
          icon={
            stage ? (
              <div
                className={classNames(
                  'vai-min-w-3 vai-px-[2px] vai-py-[1px] vai-rounded-full vai-flex vai-items-center vai-justify-center',
                  {
                    'vai-bg-yellow-400': stage === 1,
                    'vai-bg-tangerine-400': stage === 2,
                    'vai-bg-blaze-500': stage === 3 || stage === 4,
                    'vai-text-zinc-700': mode === 'dark',
                    'vai-text-zinc-50': mode === 'light',
                  },
                )}
              >
                <span className="vai-font-inter vai-text-[9px] vai-font-bold vai-leading-[100%]">
                  {stageConfig.displayStage}
                </span>
              </div>
            ) : null
          }
        />
      </TooltipTrigger>
      <TooltipContent
        className={classNames('vai-w-80 vai-pt-3 vai-pb-4', {
          'vai-text-zinc-50': mode === 'dark',
        })}
        variant={mode === 'dark' ? 'dark' : 'default'}
      >
        <p className="vai-font-inter vai-font-normal">
          <Trans>Stage </Trans>
          {stageConfig.displayStage}:{' '}
          <span className="vai-font-semibold">{stageConfig.status}</span>
        </p>
        <p
          className={classNames(
            'vai-mt-1 vai-font-inter !vai-text-[12px] !vai-font-normal',
            {
              'vai-text-zinc-300': mode === 'dark',
              'vai-text-zinc-600': mode === 'light',
            },
          )}
        >
          {stageConfig.definition}
        </p>

        {/* Stage assignment */}
        {stageConfig.summary && (
          <div className="vai-mt-3">
            <p className={sectionHeadingClassName}>
              <Trans>Stage assignment</Trans>
            </p>
            <p
              className={classNames('vai-mt-2 vai-font-inter vai-text-[12px]', {
                'vai-text-zinc-50': mode === 'dark',
                'vai-text-zinc-900': mode === 'light',
              })}
            >
              {stageConfig.summary}
            </p>
            {stageConfig.criteria && (
              <ul className="vai-mt-2 vai-list-disc vai-pl-4 vai-font-inter !vai-text-[12px] vai-font-normal">
                {stageConfig.criteria.map((criterion) => (
                  <li key={criterion}>{criterion}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="vai-mt-2 vai-flex vai-flex-col vai-gap-2">
          <a
            onClick={() => {
              trackEvent(mixpanelEvents.perioChartAAPGuidelinesClicked, {
                perioStage: stage ?? 'N/A',
                examDate,
                hasCalculusAtBalancedThreshold: hasCalculus,
                isPerioPatient,
                numberOfQuadrantsWithPerioRecs,
                toothCount,
              });
            }}
            className="vai-font-inter vai-text-[12px] vai-underline vai-text-sapphire-600 vai-flex vai-items-center vai-gap-1"
            href="https://www.perio.org/wp-content/uploads/2019/08/Staging-and-Grading-Periodontitis.pdf"
            target="_blank"
            rel="noreferrer"
          >
            <Trans>AAP staging reference</Trans>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
        </div>

        {/* SRP recommendation rules */}
        <div className="vai-mt-4">
          <p className={sectionHeadingClassName}>
            <Trans>SRP recommendation rules</Trans>
          </p>
          <p
            className={classNames('vai-mt-2 vai-font-inter vai-text-[12px]', {
              'vai-text-zinc-50': mode === 'dark',
              'vai-text-zinc-900': mode === 'light',
            })}
          >
            <Trans>
              If a perio chart is available, SRP is recommended only when CAL
              and bleeding criteria are both met.
            </Trans>
          </p>
          <ul className="vai-mt-2 vai-list-disc vai-pl-4 vai-font-inter !vai-text-[12px] vai-font-normal">
            {isLocSrpEnabled ? (
              <>
                <li>
                  <Trans>
                    For generalized SRP, mesial/distal CAL measurements must be
                    &gt;= 4 mm for 4+ teeth
                  </Trans>
                </li>
                <li>
                  <Trans>
                    For localized SRP, mesial/distal CAL measurements must be
                    &gt;= 4 mm for 2-3 teeth
                  </Trans>
                </li>
              </>
            ) : (
              <li>
                <Trans>
                  Mesial/distal CAL measurements must be &gt;= 4 mm for 4+ teeth
                </Trans>
              </li>
            )}
            <li>
              <Trans>
                Bleeding must be present on at least 10% of probing sites
              </Trans>
            </li>
          </ul>

          <div className="vai-mt-2 vai-flex vai-flex-col vai-gap-2">
            <a
              onClick={() => {
                trackEvent(mixpanelEvents.perioChartBleedingEvidenceClicked, {
                  perioStage: stage ?? 'N/A',
                  examDate,
                  hasCalculusAtBalancedThreshold: hasCalculus,
                  isPerioPatient,
                  numberOfQuadrantsWithPerioRecs,
                  toothCount,
                });
              }}
              className="vai-font-inter vai-text-[12px] vai-underline vai-text-sapphire-600 vai-flex vai-items-center vai-gap-1"
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC7687205/"
              target="_blank"
              rel="noreferrer"
            >
              <Trans>Evidence for 10% bleeding threshold</Trans>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </a>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
